import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor.js";
import { logout } from "../../app/auth/authSlice.js";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import StatusBadge from "../../components/CustomComponents/StatusBadge/StatusBadge";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/CustomComponents/Switchery/ToggleSwitch.css";
import ModernGridToolbar, {
  ModernGridToolbarFilterPill,
} from "../../components/CustomComponents/ModernGridToolbar/ModernGridToolbar.jsx";
import ModernGridPagerFooter from "../../components/CustomComponents/ModernGridToolbar/ModernGridPagerFooter.jsx";
import GridEmptyState from "../../components/CustomComponents/ModernGridToolbar/GridEmptyState.jsx";
import SortableColumnHeader from "../../components/CustomComponents/ModernGridToolbar/SortableColumnHeader.jsx";
import { getModernGridFilterPillSelectStyles } from "../../components/CustomComponents/ModernGridToolbar/modernGridToolbarSelectStyles.js";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader.js";
import { defaultSelectPortalProps } from "../staff/dropdownStyles.js";

const ManageQuotes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const filterPillSelectStyles = useMemo(
    () => getModernGridFilterPillSelectStyles(dropdownStyles, isDarkMode),
    [dropdownStyles, isDarkMode]
  );
  const themeColor = useThemeColor();

  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusConfirmQuote, setStatusConfirmQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'edit'
  const [currentQuote, setCurrentQuote] = useState({
    qID: 0,
    qDetail: "",
    qIsActive: true,
    qPriority: false,
    qSortOrder: 0,
  });

  // Filter and pagination states
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState({ value: "50", label: "50" });
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortBy, setSortBy] = useState({ value: "QSortOrder", label: "Sort Order" });
  const [sortOrder, setSortOrder] = useState({ value: "ASC", label: "Asc" });
  const [activeFilter, setActiveFilter] = useState({ value: "all", label: "All Quotes" });

  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const canReorder =
    sortBy.value === "QSortOrder" &&
    sortOrder.value === "ASC" &&
    !debouncedSearch.trim() &&
    activeFilter.value === "all";

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPageNumber(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const activeFilterOptions = [
    { value: "all", label: "All Quotes" },
    { value: "active", label: "Active Only" },
    { value: "inactive", label: "Inactive Only" },
  ];

  const fetchQuotes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_QUOTES, {
        params: {
          Search: debouncedSearch.trim() || "",
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          PageSize: pageSize.value,
          PageNumber: pageNumber,
          ActiveOnly: activeFilter.value === "active" ? true : false,
        },
      });

      if (response.data.status) {
        const quoteData = response.data.data;
        if (Array.isArray(quoteData)) {
          setQuotes(quoteData);
          setTotalRecords(response.data.totalRecords || 0);
          setTotalPages(response.data.totalPages || 1);
        } else {
          setQuotes([]);
          setTotalRecords(0);
          setTotalPages(1);
        }
      } else {
        showErrorToast(response.data.message || "Failed to fetch quotes");
        setQuotes([]);
        setTotalRecords(0);
        setTotalPages(1);
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => {
          navigate("/user/login", { replace: true });
        }, 1000);
      } else {
        showErrorToast("Failed to fetch quotes");
      }
    }
  }, [debouncedSearch, sortBy, sortOrder, pageSize, pageNumber, activeFilter, dispatch, navigate]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleOpenAddModal = () => {
    setModalMode("add");
    setCurrentQuote({
      qID: 0,
      qDetail: "",
      qIsActive: true,
      qPriority: false,
      qSortOrder: 0,
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (quote) => {
    setModalMode("edit");
    setCurrentQuote({
      qID: quote.qID,
      qDetail: quote.qDetail,
      qIsActive: quote.qIsActive,
      qPriority: quote.qPriority,
      qSortOrder: quote.qSortOrder,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentQuote({
      qID: 0,
      qDetail: "",
      qIsActive: true,
      qPriority: false,
      qSortOrder: 0,
    });
  };

  const handleInputChange = (e, field) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setCurrentQuote({ ...currentQuote, [field]: value });
  };

  const handleSaveQuote = async () => {
    if (!currentQuote.qDetail || currentQuote.qDetail.trim() === "") {
      showErrorToast("Please enter quote details");
      return;
    }

    try {
      setLoading(true);

      let payload;
      let response;

      if (modalMode === "add") {
        payload = {
          QDetail: currentQuote.qDetail,
          QIsActive: currentQuote.qIsActive,
          QPriority: currentQuote.qPriority,
          QSortOrder: parseInt(currentQuote.qSortOrder) || 0,
        };
        response = await axiosInstance.post(APIs.POST_ADD_QUOTE, payload);
      } else {
        payload = {
          QID: currentQuote.qID,
          QDetail: currentQuote.qDetail,
          QIsActive: currentQuote.qIsActive,
          QPriority: currentQuote.qPriority,
          QSortOrder: parseInt(currentQuote.qSortOrder) || 0,
        };
        response = await axiosInstance.put(APIs.PUT_UPDATE_QUOTE, payload);
      }

      if (response.data.status) {
        showSuccessToast(
          modalMode === "add"
            ? "Quote added successfully"
            : "Quote updated successfully"
        );
        handleCloseModal();
        fetchQuotes();
      } else {
        showErrorToast(
          response.data.message ||
            `Failed to ${modalMode === "add" ? "add" : "update"} quote`
        );
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast(`Failed to ${modalMode === "add" ? "add" : "update"} quote`);
    }
  };

  const handleDeleteQuote = async (qID) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will permanently delete the quote.",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        setLoading(true);
        const response = await axiosInstance.delete(APIs.DELETE_QUOTE, {
          params: { qID: qID },
        });

        if (response.data.status) {
          showSuccessToast("Quote deleted successfully");
          fetchQuotes();
        } else {
          showErrorToast(response.data.message || "Failed to delete quote");
        }
        setLoading(false);
      }
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to delete quote");
    }
  };

  const confirmQuoteStatusChange = async () => {
    if (!statusConfirmQuote) return;

    const { qID, qIsActive, qDetail } = statusConfirmQuote;
    const newStatus = !qIsActive;

    setStatusConfirmQuote(null);

    try {
      setLoading(true);
      const response = await axiosInstance.patch(
        APIs.PATCH_UPDATE_QUOTE_STATUS,
        null,
        {
          params: {
            qID,
            status: newStatus,
          },
        }
      );

      if (response.data.status) {
        const label =
          (qDetail || "").length > 40
            ? `${(qDetail || "").slice(0, 40)}...`
            : qDetail || "Quote";
        showSuccessToast(
          `"${label}" is now ${newStatus ? "Active" : "InActive"}.`
        );
        fetchQuotes();
      } else {
        showErrorToast(response.data.message || "Failed to update status");
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
      showErrorToast("Failed to update quote status");
    }
  };

  const handleSort = (field) => {
    if (sortBy.value === field) {
      setSortOrder((prev) =>
        prev.value === "ASC"
          ? { value: "DESC", label: "Desc" }
          : { value: "ASC", label: "Asc" }
      );
    } else {
      setSortBy({ value: field, label: field });
      setSortOrder({ value: "ASC", label: "Asc" });
    }
    setPageNumber(1);
  };

  const handleDragStart = (e, index) => {
    if (!canReorder) {
      e.preventDefault();
      return;
    }
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    if (!canReorder) return;
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = isDarkMode
      ? "rgba(65, 132, 143, 0.18)"
      : "rgba(65, 132, 143, 0.12)";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!canReorder) return;

    const draggedIndex = dragItem.current;
    const overIndex = dragOverItem.current;

    if (
      draggedIndex === null ||
      overIndex === null ||
      draggedIndex === undefined ||
      overIndex === undefined ||
      draggedIndex === overIndex
    ) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const previousQuotes = [...quotes];
    const orderValues = previousQuotes.map((quote) => quote.qSortOrder);
    const reordered = [...previousQuotes];
    const [draggedQuote] = reordered.splice(draggedIndex, 1);
    reordered.splice(overIndex, 0, draggedQuote);

    const updatedQuotes = reordered.map((quote, index) => ({
      ...quote,
      qSortOrder: orderValues[index],
    }));

    setQuotes(updatedQuotes);

    try {
      const response = await axiosInstance.post(APIs.POST_UPDATE_QUOTE_SORT_ORDER, {
        Updates: updatedQuotes.map((quote) => ({
          ID: quote.qID,
          SortOrder: quote.qSortOrder,
        })),
      });

      if (response.data.status) {
        showSuccessToast("Quote order updated");
        fetchQuotes();
      } else {
        showErrorToast(response.data.message || "Failed to update quote order");
        setQuotes(previousQuotes);
      }
    } catch (err) {
      console.log(err);
      showErrorToast("Failed to update quote order");
      setQuotes(previousQuotes);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tbody tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const clearFilters = () => {
    setSearch("");
    setActiveFilter({ value: "all", label: "All Quotes" });
    setPageNumber(1);
  };

  const hasFiltersApplied = () =>
    search.trim() !== "" || activeFilter.value !== "all";

  const renderNoDataMessage = () => {
    if (loading || quotes.length > 0) return null;

    return (
      <GridEmptyState
        isDarkMode={isDarkMode}
        themeColor={themeColor}
        title="No quotes found"
        description={
          hasFiltersApplied()
            ? "No quotes match your current search or status filter. Try different filters or clear them to see all quotes."
            : "There are no quotes to show right now. Add a new quote or check back later."
        }
        onClearFilters={hasFiltersApplied() ? clearFilters : undefined}
        clearFiltersLabel="Clear all filters"
      />
    );
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Quotes</li>
          </ol>
          <h1 className="page-header mb-0">Manage Quotes</h1>
        </div>

        <div className="ms-auto">
          <button
            className="btn px-4"
            onClick={handleOpenAddModal}
            style={{
              backgroundColor: themeColor,
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              border: "none",
              color: "white",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "600",
              transition: "all 0.3s ease",
              boxShadow: `0 2px 8px ${themeColor}40`,
            }}
            onMouseEnter={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }}
          >
            <i className="fas fa-plus fa-lg me-2 ms-n2 text-white"></i>
            Add Quote
          </button>
        </div>
      </div>

      <div className="mb-3 mt-2">
        <ModernGridToolbar
          isDarkMode={isDarkMode}
          title="Quotes"
          totalCount={totalRecords}
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search quotes..."
          bodyFlush
          topFilters={
            <ModernGridToolbarFilterPill
              iconClass="fa fa-toggle-on"
              className="modern-grid-toolbar__filter-pill--status"
            >
              <Select
                value={activeFilter}
                onChange={(option) => {
                  setActiveFilter(option);
                  setPageNumber(1);
                }}
                options={activeFilterOptions}
                styles={filterPillSelectStyles}
                placeholder="Filter Status"
                isSearchable={false}
                {...defaultSelectPortalProps}
              />
            </ModernGridToolbarFilterPill>
          }
        >
          <div className="px-3 pb-3">
            <GridSectionLoader loading={loading} minHeight="280px">
              {renderNoDataMessage()}

              {quotes.length > 0 && (
                <>
                  <div
                    className="table-responsive table-container"
                    style={{ minHeight: "200px", position: "relative", zIndex: 1 }}
                  >
                    <table
                      className="table table-hover align-middle"
                      style={{ "--modern-grid-sort-active": themeColor }}
                    >
                      <thead>
                        <tr>
                          <th className="pt-2 pb-2 text-center" style={{ width: "44px" }} />
                          <SortableColumnHeader
                            sortField="QDetail"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                          >
                            Quote
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="QSortOrder"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            className="text-center"
                          >
                            Order
                          </SortableColumnHeader>
                          <SortableColumnHeader
                            sortField="QPriority"
                            sortBy={sortBy}
                            sortOrder={sortOrder}
                            onSort={handleSort}
                            className="text-center"
                          >
                            Priority
                          </SortableColumnHeader>
                          <th className="pt-2 pb-2 text-center">Status</th>
                          <th className="pt-2 pb-2 text-center" style={{ width: "110px" }}>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {quotes.map((quote, index) => (
                          <tr
                            key={quote.qID}
                            onDragEnter={(e) => handleDragEnter(e, index)}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                          >
                            <td className="align-middle text-center">
                              <i
                                className="fas fa-bars"
                                style={{
                                  cursor: canReorder ? "grab" : "not-allowed",
                                  width: "28px",
                                  height: "28px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  borderRadius: "6px",
                                  border: `1px solid ${
                                    canReorder
                                      ? isDarkMode
                                        ? "#6c757d"
                                        : "#adb5bd"
                                      : isDarkMode
                                        ? "#3a4044"
                                        : "#dee2e6"
                                  }`,
                                  color: canReorder
                                    ? isDarkMode
                                      ? "#adb5bd"
                                      : "#6c757d"
                                    : isDarkMode
                                      ? "#555"
                                      : "#ced4da",
                                  backgroundColor: "transparent",
                                  opacity: canReorder ? 1 : 0.55,
                                  transition: "all 0.2s ease",
                                }}
                                draggable={canReorder}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                title={
                                  canReorder
                                    ? "Drag to reorder"
                                    : "Sort by Order ascending (clear search/filters) to reorder"
                                }
                                onMouseEnter={(e) => {
                                  if (!canReorder) return;
                                  e.currentTarget.style.backgroundColor = themeColor;
                                  e.currentTarget.style.borderColor = themeColor;
                                  e.currentTarget.style.color = "white";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.borderColor = canReorder
                                    ? isDarkMode
                                      ? "#6c757d"
                                      : "#adb5bd"
                                    : isDarkMode
                                      ? "#3a4044"
                                      : "#dee2e6";
                                  e.currentTarget.style.color = canReorder
                                    ? isDarkMode
                                      ? "#adb5bd"
                                      : "#6c757d"
                                    : isDarkMode
                                      ? "#555"
                                      : "#ced4da";
                                }}
                              />
                            </td>
                            <td
                              className="align-middle"
                              onClick={() => handleOpenEditModal(quote)}
                              style={{ cursor: "pointer", maxWidth: "560px" }}
                              title="Click to edit"
                            >
                              <div className="d-flex align-items-start gap-2">
                                <span
                                  className="d-inline-flex align-items-center justify-content-center flex-shrink-0 rounded-circle mt-1"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    backgroundColor: `${themeColor}1f`,
                                    color: themeColor,
                                    fontSize: "11px",
                                  }}
                                >
                                  <i className="fa fa-quote-left" aria-hidden></i>
                                </span>
                                <span
                                  className="flex-grow-1"
                                  style={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                    lineHeight: 1.5,
                                  }}
                                >
                                  {quote.qDetail}
                                </span>
                              </div>
                            </td>
                            <td className="align-middle text-center">
                              <span
                                className="badge rounded-pill"
                                style={{
                                  backgroundColor: isDarkMode
                                    ? "rgba(255,255,255,0.08)"
                                    : "rgba(0,0,0,0.05)",
                                  color: isDarkMode ? "#e9ecef" : "#495057",
                                  fontWeight: 600,
                                  minWidth: "34px",
                                }}
                              >
                                {quote.qSortOrder ?? 0}
                              </span>
                            </td>
                            <td className="align-middle text-center">
                              {quote.qPriority ? (
                                <span
                                  className="badge rounded-pill"
                                  style={{
                                    backgroundColor: "rgba(255, 193, 7, 0.18)",
                                    color: "#d39e00",
                                    fontWeight: 600,
                                  }}
                                >
                                  <i className="fa fa-star me-1" aria-hidden></i>
                                  Priority
                                </span>
                              ) : (
                                <span
                                  className="badge rounded-pill"
                                  style={{
                                    backgroundColor: isDarkMode
                                      ? "rgba(255,255,255,0.08)"
                                      : "rgba(108,117,125,0.15)",
                                    color: isDarkMode ? "#adb5bd" : "#6c757d",
                                    fontWeight: 600,
                                  }}
                                >
                                  Normal
                                </span>
                              )}
                            </td>
                            <td className="align-middle text-center">
                              <StatusBadge
                                isActive={quote.qIsActive}
                                onClick={() => setStatusConfirmQuote(quote)}
                              />
                            </td>
                            <td className="align-middle text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  onClick={() => handleOpenEditModal(quote)}
                                  className="btn btn-sm"
                                  title="Edit quote"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "6px",
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    backgroundColor: "transparent",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = "white";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = themeColor;
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: "12px" }}></i>
                                </button>
                                <button
                                  onClick={() => handleDeleteQuote(quote.qID)}
                                  className="btn btn-sm"
                                  title="Delete quote"
                                  disabled={loading}
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "6px",
                                    border: "1px solid #dc3545",
                                    color: "#dc3545",
                                    backgroundColor: "transparent",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "#dc3545";
                                    e.currentTarget.style.color = "white";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "transparent";
                                    e.currentTarget.style.color = "#dc3545";
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ fontSize: "12px" }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <ModernGridPagerFooter
                    pageSize={pageSize}
                    onPageSizeChange={(option) => {
                      setPageSize(option);
                      setPageNumber(1);
                    }}
                    totalRecords={totalRecords}
                    pageNumber={pageNumber}
                    totalPages={totalPages}
                    setPageNumber={setPageNumber}
                  />
                </>
              )}
            </GridSectionLoader>
          </div>
        </ModernGridToolbar>
      </div>

      {/* Add/Edit Quote Modal */}
      <CustomModal
        title={modalMode === "add" ? "Add Quote" : "Edit Quote"}
        subtitle={
          modalMode === "add"
            ? "Create a quote that can appear across the platform."
            : "Update the quote text, priority, or active status."
        }
        fontSize="18px"
        fontWeight={600}
        onClose={handleCloseModal}
        isOpen={showModal}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText={modalMode === "add" ? "Add Quote" : "Update Quote"}
        submitButtonStyle={{ width: "auto", minWidth: "140px", whiteSpace: "nowrap" }}
        onSubmit={handleSaveQuote}
        modalSize="modal-lg"
        minHeight="auto"
        maxHeight="620px"
      >
        <div className="px-1 pb-1">
          <div className="mb-3">
            <label
              className="form-label fw-semibold mb-1"
              htmlFor="quoteDetail"
              style={{ color: isDarkMode ? "#f1f5f9" : "#0f172a" }}
            >
              Quote Details <span className="text-danger">*</span>
            </label>
            <textarea
              id="quoteDetail"
              className="form-control"
              placeholder="Enter quote text..."
              value={currentQuote.qDetail}
              onChange={(e) => handleInputChange(e, "qDetail")}
              rows="5"
              style={{
                borderRadius: "10px",
                resize: "vertical",
                minHeight: "120px",
                lineHeight: 1.5,
              }}
            />
            <small
              className="d-block mt-1"
              style={{ color: isDarkMode ? "#94a3b8" : "#64748b" }}
            >
              Tip: include the author after an em dash if needed.
            </small>
          </div>

          <div className="row g-2">
            <div className="col-md-6">
              <div
                className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3 h-100"
                style={{
                  backgroundColor: isDarkMode ? "#1e2427" : "#f8fafc",
                  border: `1px solid ${
                    currentQuote.qPriority
                      ? "rgba(255, 193, 7, 0.45)"
                      : isDarkMode
                        ? "#3a4044"
                        : "#e2e8f0"
                  }`,
                }}
              >
                <div className="min-w-0 pe-2">
                  <div
                    className="fw-semibold"
                    style={{ color: isDarkMode ? "#f1f5f9" : "#0f172a", fontSize: 14 }}
                  >
                    <i
                      className="fa fa-star me-2"
                      style={{ color: currentQuote.qPriority ? "#d39e00" : undefined }}
                      aria-hidden
                    />
                    Priority Quote
                  </div>
                  <div
                    className="small mt-1"
                    style={{ color: isDarkMode ? "#94a3b8" : "#64748b", lineHeight: 1.4 }}
                  >
                    Shows ahead of normal quotes.
                  </div>
                </div>
                <ToggleSwitch
                  isChecked={currentQuote.qPriority}
                  onToggle={() =>
                    setCurrentQuote((prev) => ({
                      ...prev,
                      qPriority: !prev.qPriority,
                    }))
                  }
                  label=""
                  disabled={false}
                />
              </div>
            </div>

            <div className="col-md-6">
              <div
                className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3 h-100"
                style={{
                  backgroundColor: isDarkMode ? "#1e2427" : "#f8fafc",
                  border: `1px solid ${
                    currentQuote.qIsActive
                      ? `${themeColor}66`
                      : isDarkMode
                        ? "#3a4044"
                        : "#e2e8f0"
                  }`,
                }}
              >
                <div className="min-w-0 pe-2">
                  <div
                    className="fw-semibold"
                    style={{ color: isDarkMode ? "#f1f5f9" : "#0f172a", fontSize: 14 }}
                  >
                    <i
                      className={`fa ${
                        currentQuote.qIsActive ? "fa-circle-check" : "fa-circle-pause"
                      } me-2`}
                      style={{ color: currentQuote.qIsActive ? themeColor : undefined }}
                      aria-hidden
                    />
                    {currentQuote.qIsActive ? "Active" : "Inactive"}
                  </div>
                  <div
                    className="small mt-1"
                    style={{ color: isDarkMode ? "#94a3b8" : "#64748b", lineHeight: 1.4 }}
                  >
                    {currentQuote.qIsActive
                      ? "Visible where quotes are shown."
                      : "Hidden until reactivated."}
                  </div>
                </div>
                <ToggleSwitch
                  isChecked={currentQuote.qIsActive}
                  onToggle={() =>
                    setCurrentQuote((prev) => ({
                      ...prev,
                      qIsActive: !prev.qIsActive,
                    }))
                  }
                  label=""
                  disabled={false}
                />
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        title="Change quote status?"
        subtitle={
          statusConfirmQuote
            ? `Are you sure you want to set this quote to ${
                statusConfirmQuote.qIsActive ? "InActive" : "Active"
              }?`
            : ""
        }
        fontSize="18px"
        fontWeight={400}
        onClose={() => setStatusConfirmQuote(null)}
        isOpen={!!statusConfirmQuote}
        isCentered={true}
        showButton={true}
        showSubmitButton={true}
        showCancelButton={true}
        submitButtonText="Confirm"
        onSubmit={confirmQuoteStatusChange}
        dialogMaxWidth="400px"
        minHeight="auto"
        maxHeight="320px"
      />
    </div>
  );
};

export default ManageQuotes;
